import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Chip,
  Backdrop,
  List,
  ListItem,
  ListItemText,
  Link
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getAdminPublication,
  AdminPublication,
  deleteAdminPublication
} from '../../services/actaAdminPublicationsService';
import swal from 'sweetalert';
import { getActaRelativeFileUrl } from '../../services/actaRelativeFileService';

const AdminPublicationDetail: React.FC = () => {
  const { publicationId } = useParams<{ publicationId: string }>();
  const [pubData, setPubData] = useState<AdminPublication | null>(null);
  const [loading, setLoading] = useState(false);
  
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();

  const fetchData = async () => {
    if (!publicationId) return;
    setLoading(true);
    try {
      const result = await getAdminPublication(publicationId);
      setPubData(result);
    } catch (error) {
      console.error('Erro ao detalhar publicação admin:', error);
      swal('Erro', 'Não foi possível carregar detalhes da publicação.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicationId]);

  const handleViewPDF = () => {
    if (pubData?.pdf_path) {
      const pdfUrl = getActaRelativeFileUrl(pubData.pdf_path);
      window.open(pdfUrl, '_blank');
    } else {
      swal('Info', 'PDF não disponível.', 'info');
    }
  };

  const handleDelete = async () => {
    if (!pubData) return;

    const confirm = await swal({
      title: 'Tem certeza?',
      text: 'Esta publicação será excluída definitivamente.',
      icon: 'warning',
      buttons: ['Cancelar', 'Excluir'],
      dangerMode: true,
    });

    if (confirm) {
      setDeleting(true); // Inicia o loading
      try {
        await deleteAdminPublication(pubData.id);
        swal('Sucesso', 'Publicação excluída com sucesso.', 'success');
        navigate('/publicacoes-admin');
      } catch (error) {
        console.error('Erro ao excluir publicação:', error);
        swal('Erro', 'Não foi possível excluir a publicação.', 'error');
      } finally {
        setDeleting(false); // Finaliza o loading
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!pubData) {
    return (
      <Typography color="error">
        Publicação não encontrada ou inválida.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5">Detalhes da Publicação</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Voltar
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle2">
          <strong>ID:</strong> {pubData.id}
        </Typography>
        {/* Exibe a entidade */}
        {pubData.entity && (
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            <strong>Entidade:</strong> {pubData.entity.prefix} {pubData.entity.suffix}
          </Typography>
        )}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          <strong>Título:</strong> {pubData.title || '—'}
        </Typography>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          <strong>Resumo:</strong> {pubData.summary || '—'}
        </Typography>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          <strong>Publicado em:</strong> {pubData.published_at || '—'}
        </Typography>

        <Box sx={{ my: 2 }}>
          <Typography variant="subtitle2"><strong>Onde?</strong></Typography>
          {pubData.is_for_dom && <Chip label="DOM" color="info" variant="outlined" sx={{ mr: 1 }} />}
          {pubData.is_for_dou && <Chip label="DOU" color="warning" variant="outlined" sx={{ mr: 1 }} />}
          {pubData.is_for_jgc && <Chip label="JGC" color="error" variant="outlined" sx={{ mr: 1 }} />}
        </Box>

        <Box sx={{ my: 2 }}>
          <Typography variant="subtitle2"><strong>Categorias:</strong></Typography>
          {(pubData.categories || []).map((cat, idx) => (
            <Chip
              key={cat.id}
              label={cat.category}
              variant="outlined"
              sx={{ mr: 1, mt: 1 }}
            />
          ))}
        </Box>

        {/* Nova seção para exibir os arquivos originais */}
        {pubData.uploaded_files_details && pubData.uploaded_files_details.length > 0 && (
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}><strong>Arquivos Originais:</strong></Typography>
            <List dense disablePadding>
              {pubData.uploaded_files_details
                .sort((a, b) => a.order - b.order) // Ordena pela propriedade 'order'
                .map((file, index) => (
                  <ListItem key={index} disableGutters>
                    <ListItemText
                      primary={
                        <Link
                          href={getActaRelativeFileUrl(file.url)} // Usa a função helper para obter a URL completa
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                        >
                          {file.name}
                        </Link>
                      }
                    />
                  </ListItem>
                ))}
            </List>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button variant="contained" onClick={handleViewPDF}>
            Acessar Publicação
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Excluindo...' : 'Excluir Publicação'}
          </Button>
        </Box>
      </Paper>

      {/* Backdrop para indicar loading da exclusão */}
      <Backdrop
        open={deleting}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default AdminPublicationDetail;